import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faCheck, faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { TabChip } from "@/components/TabChip/TabChip";
import { classNames } from "@/utils/classNames";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";

import { OutboxCounts } from "../../../List/schema/outbox-counts";
import { OUTBOX_STATUS_TABS, OutboxStatusTab } from "./outboxStatusTabs";

interface OutboxStatusTabsProps {
	activeTab: OutboxStatusTab;
	onTabChange: (tab: OutboxStatusTab) => void;
	counts?: OutboxCounts;
	/** The All tab's configured include-set. */
	allInclude: OutboxStatusTab[];
	onToggleInclude: (tab: OutboxStatusTab) => void;
	/** Extension reuse escape-hatch (web rule §11) — portal target for the include menu. */
	portalContainer?: HTMLElement | null;
}

// SQL aggregates can arrive as strings despite the schema's coercion (defense in
// depth per the repo's counts footgun) — never sum raw count fields.
const toCount = (value?: number): number => Number(value ?? 0) || 0;

const countOf = (counts: OutboxCounts | undefined, tab: OutboxStatusTab): number | undefined => {
	if (!counts) {
		return undefined;
	}

	switch (tab) {
		case OutboxStatusTab.SCHEDULED:
			return toCount(counts.scheduled);
		case OutboxStatusTab.IN_PROGRESS:
			return toCount(counts.inProgress);
		case OutboxStatusTab.ENDED:
			return toCount(counts.ended);
		case OutboxStatusTab.PAUSED:
			return toCount(counts.paused);
		case OutboxStatusTab.ERROR:
			return toCount(counts.error);
		default:
			return undefined;
	}
};

/** The All badge is the client-side sum of its included buckets (mutually exclusive). */
export const sumAllInclude = (
	counts: OutboxCounts | undefined,
	allInclude: OutboxStatusTab[]
): number | undefined => {
	if (!counts) {
		return undefined;
	}

	return allInclude.reduce((sum, tab) => sum + (countOf(counts, tab) ?? 0), 0);
};

/**
 * The Outbox status-tab strip (PREC-3850): All (with its configurable include
 * menu) plus the five mutually-exclusive backend buckets, each with a live count.
 */
export const OutboxStatusTabs: FunctionComponent<OutboxStatusTabsProps> = ({
	activeTab,
	onTabChange,
	counts,
	allInclude,
	onToggleInclude,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const [menuOpen, setMenuOpen] = useState(false);
	const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
	const allTabRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(menuRef, menuOpen);

	useEffect(() => {
		if (!menuOpen) {
			return;
		}

		const updatePosition = () => {
			const rect = allTabRef.current?.getBoundingClientRect();

			if (rect) {
				setMenuPosition({ top: rect.bottom + 4, left: rect.left });
			}
		};

		updatePosition();

		const handleOutside = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (
				target instanceof Node &&
				!menuRef.current?.contains(target) &&
				!allTabRef.current?.contains(target)
			) {
				setMenuOpen(false);
			}
		};

		document.addEventListener("mousedown", handleOutside);
		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			document.removeEventListener("mousedown", handleOutside);
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [menuOpen]);

	const tabClasses = (tab: OutboxStatusTab, isActive: boolean): string =>
		classNames(
			"flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer",
			isActive
				? tab === OutboxStatusTab.ERROR
					? "border-status-error text-status-error"
					: "border-brand-light text-brand-light"
				: tab === OutboxStatusTab.ERROR && (countOf(counts, tab) ?? 0) > 0
					? "border-transparent text-status-error hover:border-status-error/50"
					: "border-transparent text-text-meta hover:text-text hover:border-gray-300"
		);

	return (
		<>
			{OUTBOX_STATUS_TABS.map((tab) => {
				const isActive = activeTab === tab;
				const count =
					tab === OutboxStatusTab.ALL ? sumAllInclude(counts, allInclude) : countOf(counts, tab);

				return (
					<button
						key={tab}
						ref={tab === OutboxStatusTab.ALL ? allTabRef : undefined}
						className={tabClasses(tab, isActive)}
						data-testid={`outbox-tab-${tab}`}
						type="button"
						onClick={() => {
							if (tab === OutboxStatusTab.ALL && isActive) {
								setMenuOpen((open) => !open);

								return;
							}

							onTabChange(tab);
						}}
					>
						<span>{t(`outbox.statusTabs.${tab}`)}</span>
						{typeof count === "number" && <TabChip active={isActive} label={`${count}`} />}
						{tab === OutboxStatusTab.ALL && (
							<Icon className="w-2.5 h-2.5 opacity-60" icon={faChevronDown} />
						)}
					</button>
				);
			})}
			{menuOpen &&
				menuPosition &&
				ReactDOM.createPortal(
					<div
						ref={menuRef}
						className="fixed z-[1200] min-w-[230px] bg-white border border-border-light rounded-[10px] shadow-lg p-1.5"
						data-testid="outbox-all-include-menu"
						style={{ top: menuPosition.top, left: menuPosition.left }}
					>
						<div className="px-2.5 py-1.5 text-[10.5px] font-bold uppercase tracking-wide text-gray-500">
							{t("outbox.allInclude.hint")}
						</div>
						{OUTBOX_STATUS_TABS.filter((tab) => tab !== OutboxStatusTab.ALL).map((tab) => {
							const included = allInclude.includes(tab);

							return (
								<button
									key={tab}
									className="flex items-center gap-2 w-full px-2.5 py-2 rounded-[7px] text-sm font-medium text-left cursor-pointer hover:bg-brand-white"
									data-testid={`outbox-include-${tab}`}
									type="button"
									onClick={() => onToggleInclude(tab)}
								>
									<span className="grow">{t(`outbox.statusTabs.${tab}`)}</span>
									<span className="text-xs font-bold text-gray-500">
										{countOf(counts, tab) ?? ""}
									</span>
									<Icon
										className={classNames("w-3 h-3 text-brand-light", included ? "" : "opacity-0")}
										icon={faCheck}
									/>
								</button>
							);
						})}
						<div className="h-px bg-border-light my-1 mx-1" />
						<div className="px-2.5 pb-1.5 pt-0.5 text-[11px] leading-snug text-gray-500">
							{t("outbox.allInclude.note")}
						</div>
					</div>,
					portalContainer ?? document.body
				)}
		</>
	);
};
